import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { 
  Search, Package, MapPin, Calendar, Loader2, X, Warehouse, Bell, Send
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    
    return () => clearTimeout(handler);
  }, [value, delay]);
  
  return debouncedValue;
};

// Format date helper
const formatDate = (dateStr) => {
  if (!dateStr || dateStr === 'N/A') return 'N/A';
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric'
    });
  } catch {
    return dateStr;
  }
};

const SalespersonView = () => {
  const { getAuthHeaders, user, logout } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [justSelected, setJustSelected] = useState(false);
  const [canSendAlerts, setCanSendAlerts] = useState(true);
  const [assignedDepot, setAssignedDepot] = useState(null);
  
  // Alert modal state
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [alertItem, setAlertItem] = useState(null);
  const [alertMessage, setAlertMessage] = useState('');
  const [sendingAlert, setSendingAlert] = useState(false);
  
  const debouncedQuery = useDebounce(searchQuery, 300);

  // Fetch autocomplete suggestions
  useEffect(() => {
    const fetchSuggestions = async () => {
      // Don't fetch suggestions if we just selected one or have search results
      if (justSelected) {
        setJustSelected(false);
        return;
      }
      
      if (!debouncedQuery || debouncedQuery.length < 1) {
        setSuggestions([]);
        setShowSuggestions(false);
        return;
      }
      
      // Don't show autocomplete if we already have search results for this query
      if (searchResults.length > 0) {
        setSuggestions([]);
        setShowSuggestions(false);
        return;
      }
      
      try {
        const response = await axios.get(
          `${API}/salesperson/autocomplete?q=${encodeURIComponent(debouncedQuery)}`,
          { headers: getAuthHeaders() }
        );
        setSuggestions(response.data.suggestions || []);
        // Only show suggestions if we don't have search results
        if (searchResults.length === 0) {
          setShowSuggestions(true);
        }
      } catch (error) {
        console.error('Error fetching suggestions:', error);
      }
    };
    
    fetchSuggestions();
  }, [debouncedQuery, getAuthHeaders, justSelected, searchResults.length]);

  // Search for item stock
  const handleSearch = useCallback(async (query) => {
    if (!query || query.length < 2) {
      toast.error('Please enter at least 2 characters');
      return;
    }
    
    setLoading(true);
    setShowSuggestions(false);
    setSelectedItem(null);
    
    try {
      const response = await axios.get(
        `${API}/salesperson/search?q=${encodeURIComponent(query)}`,
        { headers: getAuthHeaders() }
      );
      setSearchResults(response.data.items || []);
      setCanSendAlerts(response.data.can_send_alerts !== false);
      setAssignedDepot(response.data.assigned_depot || null);
      
      if (response.data.items?.length === 0) {
        toast.info('No items found matching your search');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Search failed');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  // Handle suggestion click
  const handleSuggestionClick = (suggestion) => {
    // Immediately hide suggestions and prevent refetch
    setJustSelected(true);
    setSuggestions([]);
    setShowSuggestions(false);
    setSearchQuery(suggestion);
    
    // Then perform search
    handleSearch(suggestion);
  };

  // Handle item card click to expand
  const handleItemClick = (item) => {
    setSelectedItem(selectedItem?.item_name === item.item_name ? null : item);
  };

  // Handle form submit
  const handleSubmit = (e) => {
    e.preventDefault();
    handleSearch(searchQuery);
  };

  // Clear search
  const clearSearch = () => {
    setSearchQuery('');
    setSuggestions([]);
    setSearchResults([]);
    setSelectedItem(null);
  };

  // Open alert modal
  const openAlertModal = (item, e) => {
    e.stopPropagation(); // Prevent card click
    setAlertItem(item);
    setAlertMessage('');
    setShowAlertModal(true);
  };

  // Send stock alert
  const handleSendAlert = async () => {
    if (!alertMessage.trim()) {
      toast.error('Please enter a message');
      return;
    }

    setSendingAlert(true);
    try {
      const response = await axios.post(
        `${API}/salesperson/send-alert`,
        {
          item_name: alertItem.item_name,
          sku: alertItem.sku,
          message: alertMessage,
          depot_stocks: alertItem.depots.map(d => ({
            depot_name: d.depot_name,
            quantity: d.quantity
          }))
        },
        { headers: getAuthHeaders() }
      );
      toast.success(`Alert sent successfully to ${response.data.sent_to}`);
      setShowAlertModal(false);
      setAlertItem(null);
      setAlertMessage('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send alert');
    } finally {
      setSendingAlert(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header - Fixed on mobile */}
      <div className="sticky top-0 z-50 bg-white border-b shadow-sm">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Warehouse className="w-6 h-6 text-blue-600" />
              <span className="font-bold text-lg text-slate-900">Stock Lookup</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-500">{user?.name}</span>
              <button
                onClick={logout}
                className="text-xs text-red-500 hover:text-red-700 px-2 py-1 rounded"
              >
                Logout
              </button>
            </div>
          </div>
          
          {/* Assigned Depot Badge */}
          {(user?.depot_name || assignedDepot) && (
            <div className="mb-3 flex items-center gap-2">
              <Badge className="bg-blue-100 text-blue-700 border-0">
                <MapPin className="w-3 h-3 mr-1" />
                {user?.depot_name || assignedDepot}
              </Badge>
              <span className="text-xs text-slate-500">Viewing stock for this depot only</span>
            </div>
          )}
          
          {/* Search Form */}
          <form onSubmit={handleSubmit} className="relative">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                type="text"
                placeholder="Search item name..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  if (e.target.value.length > 0) {
                    setShowSuggestions(true);
                  }
                }}
                onFocus={() => {
                  if (suggestions.length > 0 && searchResults.length === 0) {
                    setShowSuggestions(true);
                  }
                }}
                onBlur={() => {
                  // Delay hiding to allow click on suggestion
                  setTimeout(() => setShowSuggestions(false), 200);
                }}
                className="pl-10 pr-10 h-12 text-base rounded-xl border-slate-200 focus:border-blue-500"
                autoComplete="off"
                data-testid="salesperson-search-input"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={clearSearch}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>
            
            {/* Autocomplete Suggestions */}
            {showSuggestions && suggestions.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-xl shadow-lg border z-50 max-h-60 overflow-y-auto">
                {suggestions.map((suggestion, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => handleSuggestionClick(suggestion)}
                    className="w-full px-4 py-3 text-left hover:bg-slate-50 text-sm border-b last:border-0 flex items-center gap-2"
                  >
                    <Search className="w-4 h-4 text-slate-400" />
                    <span className="truncate">{suggestion}</span>
                  </button>
                ))}
              </div>
            )}
          </form>
        </div>
      </div>

      {/* Content Area */}
      <div className="p-4 pb-20">
        {/* Loading State */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-blue-500 mb-3" />
            <p className="text-slate-500">Searching...</p>
          </div>
        )}

        {/* Empty State */}
        {!loading && searchResults.length === 0 && !searchQuery && (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <Package className="w-16 h-16 text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-700 mb-2">Search for Items</h3>
            <p className="text-sm text-slate-500 max-w-xs">
              Enter an item name to see stock availability across all depots
            </p>
          </div>
        )}

        {/* No Results */}
        {!loading && searchResults.length === 0 && searchQuery && debouncedQuery.length >= 2 && (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <Search className="w-16 h-16 text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-700 mb-2">No Items Found</h3>
            <p className="text-sm text-slate-500">
              Try a different search term
            </p>
          </div>
        )}

        {/* Search Results */}
        {!loading && searchResults.length > 0 && (
          <div className="space-y-3">
            <p className="text-sm text-slate-500 mb-2">
              Found {searchResults.length} item{searchResults.length > 1 ? 's' : ''}
            </p>
            
            {searchResults.map((item) => {
              const isExpanded = selectedItem?.item_name === item.item_name;
              
              return (
                <Card 
                  key={item.item_name}
                  className={`transition-all ${isExpanded ? 'ring-2 ring-blue-400' : ''}`}
                  onClick={() => handleItemClick(item)}
                  data-testid={`item-card-${item.sku}`}
                >
                  <CardContent className="p-4">
                    {/* Item Header */}
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-slate-900 leading-tight">
                          {item.item_name}
                        </h3>
                        <p className="text-xs text-slate-500 mt-1">SKU: {item.sku}</p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-2xl font-bold text-blue-600">
                          {item.total_stock.toLocaleString()}
                        </p>
                        <p className="text-xs text-slate-500">Total Stock</p>
                      </div>
                    </div>

                    {/* Depot Count Badge */}
                    <div className="mt-3 flex items-center gap-2">
                      <Badge variant="secondary" className="bg-slate-100">
                        <MapPin className="w-3 h-3 mr-1" />
                        {item.depots.length} depot{item.depots.length > 1 ? 's' : ''}
                      </Badge>
                      {!isExpanded && (
                        <span className="text-xs text-blue-500">Tap for details</span>
                      )}
                    </div>

                    {/* Expanded Depot Details */}
                    {isExpanded && (
                      <div className="mt-4 pt-4 border-t space-y-2">
                        <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-3">
                          Stock by Depot
                        </p>
                        {item.depots.map((depot, idx) => (
                          <div 
                            key={idx}
                            className="flex items-center justify-between py-2 px-3 bg-slate-50 rounded-lg"
                          >
                            <div className="flex items-center gap-2">
                              <MapPin className="w-4 h-4 text-slate-400" />
                              <span className="font-medium text-slate-700">{depot.depot_name}</span>
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-slate-900">
                                {depot.quantity.toLocaleString()}
                              </p>
                              <div className="flex items-center gap-1 text-xs text-slate-400">
                                <Calendar className="w-3 h-3" />
                                {formatDate(depot.upload_date)}
                              </div>
                            </div>
                          </div>
                        ))}
                        
                        {/* Total Row */}
                        <div className="flex items-center justify-between py-2 px-3 bg-blue-50 rounded-lg mt-2">
                          <span className="font-medium text-blue-700">Total</span>
                          <span className="font-bold text-blue-700">
                            {item.total_stock.toLocaleString()}
                          </span>
                        </div>

                        {/* Send Alert Button - Only show if user can send alerts (all depots access) */}
                        {canSendAlerts && (
                          <Button
                            onClick={(e) => openAlertModal(item, e)}
                            className="w-full mt-3 bg-orange-500 hover:bg-orange-600 text-white"
                            data-testid={`send-alert-btn-${item.sku}`}
                          >
                            <Bell className="w-4 h-4 mr-2" />
                            Send Stock Alert
                          </Button>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Stock Alert Modal */}
      <Dialog open={showAlertModal} onOpenChange={setShowAlertModal}>
        <DialogContent className="sm:max-w-md mx-4">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-orange-500" />
              Send Stock Alert
            </DialogTitle>
          </DialogHeader>
          
          {alertItem && (
            <div className="space-y-4">
              {/* Item Info */}
              <div className="bg-slate-50 p-3 rounded-lg">
                <p className="font-semibold text-slate-900">{alertItem.item_name}</p>
                <p className="text-xs text-slate-500">SKU: {alertItem.sku}</p>
                <div className="flex items-center gap-2 mt-2">
                  <Badge variant="secondary" className="bg-blue-100 text-blue-700">
                    Total: {alertItem.total_stock.toLocaleString()}
                  </Badge>
                  <Badge variant="secondary" className="bg-slate-100">
                    {alertItem.depots.length} depots
                  </Badge>
                </div>
              </div>

              {/* Depot Stock Summary */}
              <div className="space-y-1">
                <p className="text-sm font-medium text-slate-700">Stock by Depot:</p>
                <div className="max-h-32 overflow-y-auto space-y-1">
                  {alertItem.depots.map((depot, idx) => (
                    <div key={idx} className="flex justify-between text-sm px-2 py-1 bg-slate-50 rounded">
                      <span className="text-slate-600">{depot.depot_name}</span>
                      <span className="font-medium">{depot.quantity.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Message Input */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  Your Message <span className="text-red-500">*</span>
                </label>
                <Textarea
                  placeholder="E.g., Customer needs 50 units urgently. Item not available at nearby depot. Please arrange transfer or restock."
                  value={alertMessage}
                  onChange={(e) => setAlertMessage(e.target.value)}
                  rows={3}
                  className="resize-none"
                  data-testid="alert-message-input"
                />
                <p className="text-xs text-slate-500">
                  This message will be sent to the admin along with your details and stock information.
                </p>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setShowAlertModal(false)}
              disabled={sendingAlert}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSendAlert}
              disabled={sendingAlert || !alertMessage.trim()}
              className="bg-orange-500 hover:bg-orange-600"
              data-testid="confirm-send-alert-btn"
            >
              {sendingAlert ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Alert
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SalespersonView;
